import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
} from "@react-pdf/renderer";
import { RelatorioStyles as styles } from "./styles";
import { GetRecibos, ReciboDoacaoPDFProps } from "../../shared/dtos/Recibo.dto";
import { formatCurrency } from "../../utils/formatCurrency/formatCurrency";
import { formatarData } from "../../utils/dateUtils/dateUtils";

// Estilos extras para linha de corte e metade da página
const extraStyles = StyleSheet.create({
  hr: {
    borderBottomWidth: 1,
    borderStyle: "dashed",
    borderBottomColor: "#000",
    marginVertical: 10, // reduzido
    width: "100%",
  },
  halfPage: {
    minHeight: "45%",
    maxHeight: "46%", // diminua aqui se necessário
    justifyContent: "flex-start",
    padding: 0, // removido padding extra
    margin: 0, // removido margin extra
  },
  cellHeaderSmall: {
    fontSize: 6.5, // reduzido,
    backgroundColor: "#f0f0f0",
    fontWeight: "bold",
    padding: "0.5px", // reduzido
    border: "1px solid #000",
    textAlign: "center",
    alignContent: "center",
  },
  cellSmall: {
    fontSize: 6.5, // reduzido
    padding: "0.5px", // reduzido
    border: "1px solid #000",
    textAlign: "right"
  },
  cellSmallLeftAlign: {
    fontSize: 6.5, // reduzido
    padding: "0.5px", // reduzido
    border: "1px solid #000",
    textAlign: "left"
  },
});

// Colunas completas para o recibo
const columnStyles = [
  { width: "8%" }, // Nº Boleto
  { width: "5%" }, // Parcela
  { width: "20%" }, // Receita
  { width: "10%" }, // Vencimento
  { width: "10%" }, // Valor
  { width: "8%" }, // Outras Receitas
  { width: "7%" }, // Multa
  { width: "9%" }, // Juros
  { width: "8%" }, // Outros Acréscimos
  { width: "8%" }, // Desc. Pontualidade
  { width: "9%" }, // Desc. Outros
  { width: "10%" }, // Total
];

// Função utilitária para formatação robusta de datas
const formatarDataRobusta = (data: string | null): string => {
  if (!data) return "";
  let dateObj = new Date(data);
  if (!isNaN(dateObj.getTime())) {
    const dia = String(dateObj.getDate()).padStart(2, "0");
    const mes = String(dateObj.getMonth() + 1).padStart(2, "0");
    const ano = dateObj.getFullYear();
    return `${dia}/${mes}/${ano}`;
  }
  const regex = /(\d{2,4})[-\/](\d{2})[-\/](\d{2,4})/;
  const match = data.match(regex);
  if (match) {
    let [_, a, b, c] = match;
    if (a.length === 4) return `${c}/${b}/${a}`;
    if (c.length === 4) return `${a}/${b}/${c}`;
  }
  return "";
};

// Componente para meia página do recibo
const ReciboHalf: React.FC<{
  recibo: GetRecibos;
  cpf: string;
  usuario: string;
  responsavel: string;
}> = ({ recibo, cpf, usuario, responsavel }) => {
  const valorTotal = recibo.fichas.reduce(
    (acc, p) =>
      acc +
      (Number(p.valorParcela) ?? 0) +
      (Number(p.valorAjuste) ?? 0) ,
    0
  );
  const valorFormated = formatCurrency(valorTotal.toString());
  // Função simples para valor por extenso (exemplo)
  function valorPorExtenso(valor: number): string {
    const unidades = [
      "",
      "um",
      "dois",
      "três",
      "quatro",
      "cinco",
      "seis",
      "sete",
      "oito",
      "nove",
    ];
    const especiais = [
      "dez",
      "onze",
      "doze",
      "treze",
      "quatorze",
      "quinze",
      "dezesseis",
      "dezessete",
      "dezoito",
      "dezenove",
    ];
    const dezenas = [
      "",
      "",
      "vinte",
      "trinta",
      "quarenta",
      "cinquenta",
      "sessenta",
      "setenta",
      "oitenta",
      "noventa",
    ];
    const centenas = [
      "",
      "cento",
      "duzentos",
      "trezentos",
      "quatrocentos",
      "quinhentos",
      "seiscentos",
      "setecentos",
      "oitocentos",
      "novecentos",
    ];

    let inteiro = Math.floor(valor);
    let centavos = Math.round((valor - inteiro) * 100);

    if (inteiro === 0 && centavos === 0) return "zero reais";
    if (inteiro === 100 && centavos === 0) return "cem reais";

    let extenso = "";

    // Handle case where value is less than 1
    if (inteiro === 0) {
      extenso = "zero reais";
    } else {
      // Milhares
      if (inteiro >= 1000) {
        const milhar = Math.floor(inteiro / 1000);
        if (milhar === 1) {
          extenso += "mil";
        } else {
          extenso += `${unidades[milhar]} mil`;
        }
        inteiro %= 1000;
        if (inteiro > 0) extenso += " e ";
      }

      // Centenas
      if (inteiro >= 100) {
        extenso += centenas[Math.floor(inteiro / 100)];
        inteiro %= 100;
        if (inteiro > 0) extenso += " e ";
      }

      // Dezenas especiais (10–19)
      if (inteiro >= 10 && inteiro < 20) {
        extenso += especiais[inteiro - 10];
      } else {
        // Dezenas comuns (20+)
        if (inteiro >= 20) {
          extenso += dezenas[Math.floor(inteiro / 10)];
          inteiro %= 10;
          if (inteiro > 0) extenso += " e ";
        }

        // Unidades
        if (inteiro > 0 && inteiro < 10) {
          extenso += unidades[inteiro];
        }
      }

      extenso += " reais";
    }

    // Add cents
    if (centavos > 0) {
      extenso += " e ";
      if (centavos >= 10 && centavos < 20) {
        extenso += especiais[centavos - 10];
      } else {
        const dezena = Math.floor(centavos / 10);
        const unidade = centavos % 10;

        if (dezena > 0) {
          extenso += dezenas[dezena];
          if (unidade > 0) extenso += " e ";
        }

        if (unidade > 0 || dezena === 0) {
          extenso += unidades[unidade];
        }
      }
      extenso += " centavos";
    }

    return extenso;
  }

  // Dentro do componente ReciboHalf, ajuste margens dos containers:
  return (
    <View style={extraStyles.halfPage}>
      {/* Cabeçalho */}
      <View style={{ ...styles.header, marginBottom: 2 }}>
        {" "}
        {/* reduzido */}
        <View style={styles.logoContainer}>
          <Image
            src={require("../../assets/logo-imagem.png")}
            style={styles.logo}
          />
        </View>
        <View style={styles.headerText}>
          <Text style={styles.institution}>
            Associação Beneficente Nossa Senhora da Conceição
          </Text>
          <Text style={styles.details}>
            Santa Casa de Misericórdia e Maternidade Nossa Senhora da Conceição
          </Text>
          <Text style={styles.details}>
            CNPJ 47.531.835/0001-31 Inscrição Estadual: isenta
          </Text>
          <Text style={styles.details}>
            Avenida Padre Rodolfo, 320 - Alto do Cruzeiro - 12530-000 - Cunha-SP
          </Text>
          <Text style={styles.title}>
            RECIBO Nr.{" "}
            {recibo.numeroSequencial ? `${recibo.numeroSequencial}` : ""}
          </Text>
        </View>
      </View>

      {/* Tabela de informações do recibo com células separadas */}
      <View
        style={{ display: "flex", flexDirection: "column", marginBottom: 1 }}
      >
        {" "}
        {/* reduzido */}
        <View style={{ flexDirection: "row" }}>
          <View
            style={{
              ...extraStyles.cellSmallLeftAlign,
              borderRight: "1px solid #000",
              flex: 3,
            }}
          >
            <Text style={{ fontSize: 6 }}>Responsável Financeiro</Text>
            <Text style={{ fontSize: 10 }}>{responsavel}</Text>
          </View>
          <View style={{ ...extraStyles.cellSmallLeftAlign, flex: 1 }}>
            <Text style={{ fontSize: 6 }}>CPF</Text>
            <Text style={{ fontSize: 10 }}>{cpf}</Text>
          </View>
        </View>
        <View style={{ flexDirection: "row" }}>
          <View style={{ ...extraStyles.cellSmallLeftAlign, flex: 1 }}>
            <Text style={{ fontSize: 6 }}>Recebemos a quantia de</Text>
            <Text style={{ fontSize: 10 }}>
              {valorFormated} ({valorPorExtenso(valorTotal)})
            </Text>
          </View>
        </View>
      </View>

      {/* Título centralizado para a tabela principal */}
      <View style={{ marginBottom: 1, marginTop: 1 }}>
        {" "}
        {/* reduzido */}
        <Text style={{ textAlign: "center", fontSize: 10 }}>
          {" "}
          {/* reduzido */}
          Referente(s) ao(s) boleto(s) discriminado(s) abaixo:
        </Text>
      </View>

      {/* Tabela de parcelas (completa) */}
      <View style={styles.table}>
        <View style={styles.row}>
          <Text
            style={{
              ...styles.cellHeader,
              ...extraStyles.cellHeaderSmall,
              ...columnStyles[0],
            }}
          >
            Número Boleto
          </Text>
          <Text
            style={{
              ...styles.cellHeader,
              ...extraStyles.cellHeaderSmall,
              ...columnStyles[1],
            }}
          >
            Parc.
          </Text>
          <Text
            style={{
              ...styles.cellHeader,
              ...extraStyles.cellHeaderSmall,
              ...columnStyles[2],
            }}
          >
            Receita
          </Text>
          <Text
            style={{
              ...styles.cellHeader,
              ...extraStyles.cellHeaderSmall,
              ...columnStyles[3],
            }}
          >
            Data Venc.
          </Text>
          <Text
            style={{
              ...styles.cellHeader,
              ...extraStyles.cellHeaderSmall,
              ...columnStyles[4],
            }}
          >
            Valor
          </Text>
          <Text
            style={{
              ...styles.cellHeader,
              ...extraStyles.cellHeaderSmall,
              ...columnStyles[5],
            }}
          >
            Outras Receitas
          </Text>
          <Text
            style={{
              ...styles.cellHeader,
              ...extraStyles.cellHeaderSmall,
              ...columnStyles[6],
            }}
          >
            Multa
          </Text>
          <Text
            style={{
              ...styles.cellHeader,
              ...extraStyles.cellHeaderSmall,
              ...columnStyles[7],
            }}
          >
            Juros
          </Text>
          <Text
            style={{
              ...styles.cellHeader,
              ...extraStyles.cellHeaderSmall,
              ...columnStyles[8],
            }}
          >
            Outros Acréscim.
          </Text>
          <Text
            style={{
              ...styles.cellHeader,
              ...extraStyles.cellHeaderSmall,
              ...columnStyles[9],
            }}
          >
            Desc. Pontuali.
          </Text>
          <Text
            style={{
              ...styles.cellHeader,
              ...extraStyles.cellHeaderSmall,
              ...columnStyles[10],
            }}
          >
            Desc. Outros
          </Text>
          <Text
            style={{
              ...styles.cellHeader,
              ...extraStyles.cellHeaderSmall,
              ...columnStyles[11],
            }}
          >
            Total
          </Text>
        </View>
        {recibo.fichas.map((p) => (
          <View style={styles.row} key={p.id}>
            <Text
              style={{
                ...styles.cell,
                ...extraStyles.cellSmall,
                ...columnStyles[0],
              }}
            >
              {p.id}
            </Text>
            <Text
              style={{
                ...styles.cell,
                ...extraStyles.cellSmall,
                ...columnStyles[1],
                textAlign: "center", // Added textAlign center
              }}
            >
              {p.numeroParcela}
            </Text>
            <Text
              style={{
                ...styles.cell,
                ...extraStyles.cellSmallLeftAlign,
                ...columnStyles[2],
              }}
            >
              {p.tipoCobranca} {p.anoReceitaAtual}-{p.anoReceitaFinal}
            </Text>
            <Text
              style={{
                ...styles.cell,
                ...extraStyles.cellSmall,
                ...columnStyles[3],
              }}
            >
              {formatarData(p.dataVencimento)}
            </Text>
            <Text
              style={{
                ...styles.cell,
                ...extraStyles.cellSmall,
                ...columnStyles[4],
              }}
            >
              {formatCurrency(p.valorParcela) ?? ""}
            </Text>
            <Text
              style={{
                ...styles.cell,
                ...extraStyles.cellSmall,
                ...columnStyles[5],
              }}
            >
              {formatCurrency("0") ?? ""}
            </Text>
            <Text
              style={{
                ...styles.cell,
                ...extraStyles.cellSmall,
                ...columnStyles[6],
              }}
            >
              {formatCurrency(p.valorTarifa) ?? ""}
            </Text>
            <Text
              style={{
                ...styles.cell,
                ...extraStyles.cellSmall,
                ...columnStyles[7],
              }}
            >
              {formatCurrency(p.valorAjuste) ?? ""}
            </Text>
            <Text
              style={{
                ...styles.cell,
                ...extraStyles.cellSmall,
                ...columnStyles[8],
              }}
            >
              {formatCurrency("0") ?? ""}
            </Text>
            <Text
              style={{
                ...styles.cell,
                ...extraStyles.cellSmall,
                ...columnStyles[9],
              }}
            >
              {formatCurrency("0") ?? ""}
            </Text>
            <Text
              style={{
                ...styles.cell,
                ...extraStyles.cellSmall,
                ...columnStyles[10],
              }}
            >
              {formatCurrency("0") ?? ""}
            </Text>
            <Text
              style={{
                ...styles.cell,
                ...extraStyles.cellSmall,
                ...columnStyles[11],
              }}
            >
              {formatCurrency(p.valorPago) ?? ""}
            </Text>
          </View>
        ))}
        {/* Linha de total: 11 células invisíveis e o total na última coluna */}
        <View style={styles.row}>
          {Array.from({ length: 11 }).map((_, idx) => (
            <Text
              key={idx}
              style={{
                width: columnStyles[idx].width,
                fontSize: 1,
                color: "transparent",
                backgroundColor: "transparent",
                padding: 0,
                margin: 0,
                // Não aplique border aqui!
              }}
            ></Text>
          ))}
          <Text
            style={{
              ...styles.cellHeader,
              ...extraStyles.cellHeaderSmall,
              ...columnStyles[11],
              backgroundColor: "#e0e0e0",
            }}
          >
            {valorFormated}
          </Text>
        </View>
      </View>

      {/* Título centralizado para a tabela de pagamento */}
      <View style={{ marginBottom: 1, marginTop: 1 }}>
        {" "}
        {/* reduzido */}
        <Text style={{ textAlign: "center", fontSize: 10 }}>
          {" "}
          {/* reduzido */}
          Forma de Pagamento
        </Text>
      </View>

      {/* Nova tabela de pagamento */}
      <View
        style={{ display: "flex", flexDirection: "column", marginBottom: 4 }}
      >
        {" "}
        {/* reduzido */}
        {/* Cabeçalho */}
        <View style={{ flexDirection: "row" }}>
          <View
            style={{
              ...extraStyles.cellHeaderSmall,
              borderRight: "1px solid #000",
              flex: 1.5,
            }}
          >
            <Text>Tipo do Pagamento</Text>
          </View>
          <View
            style={{
              ...extraStyles.cellHeaderSmall,
              borderRight: "1px solid #000",
              flex: 0.8,
            }}
          >
            <Text>Data Pagamento</Text>
          </View>
          <View
            style={{
              ...extraStyles.cellHeaderSmall,
              borderRight: "1px solid #000",
              flex: 0.7,
            }}
          >
            <Text>Valor</Text>
          </View>
          <View style={{ ...extraStyles.cellHeaderSmall, flex: 3 }}>
            <Text>Observações</Text>
          </View>
        </View>
        {/* Linha de dados */}
        <View style={{ flexDirection: "row" }}>
          <View
            style={{
              ...extraStyles.cellSmall,
              borderRight: "1px solid #000",
              flex: 1.5,
            }}
          >
            <Text style={{ fontSize: 8 }}>
              {typeof recibo.fichas[0].formaPagamento === "string"
                ? recibo.fichas[0].formaPagamento
                : (recibo.fichas[0].formaPagamento as { nome: string })?.nome ||
                  ""}
            </Text>
          </View>
          <View
            style={{
              ...extraStyles.cellSmall,
              borderRight: "1px solid #000",
              flex: 0.8,
            }}
          >
            <Text style={{ fontSize: 8 }}>
              {recibo.fichas[0].dataPagamento
                ? formatarData(recibo.fichas[0].dataPagamento)
                : ""}
            </Text>
          </View>
          <View
            style={{
              ...extraStyles.cellSmall,
              borderRight: "1px solid #000",
              flex: 0.7,
            }}
          >
            <Text style={{ fontSize: 8 }}>{valorFormated}</Text>
          </View>
          <View style={{ ...extraStyles.cellSmall, flex: 3 }}>
            <Text style={{ fontSize: 8 }}></Text>
          </View>
        </View>
      </View>
      {/* Rodapé */}
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "flex-end",
          marginTop: 24,
        }}
      >
        {/* Esquerda: Data e local */}
        <View style={{ flex: 1 }}>
          <Text style={{ fontSize: 10 }}>
            {(() => {
              const date = new Date();
              const day = date.getDate();
              const months = [
                "janeiro",
                "fevereiro",
                "março",
                "abril",
                "maio",
                "junho",
                "julho",
                "agosto",
                "setembro",
                "outubro",
                "novembro",
                "dezembro",
              ];
              const month = months[date.getMonth()];
              const year = date.getFullYear();
              return `Cunha-SP, ${day} de ${month} de ${year}.`;
            })()}
          </Text>
        </View>
        {/* Direita: Assinatura centralizada */}
        <View style={{ flex: 1, alignItems: "center" }}>
          <View
            style={{
              borderBottom: "1px solid #000",
              width: 140,
              marginBottom: 2,
            }}
          />
          <Text style={{ fontSize: 8, textAlign: "center", width: 140 }}>
            {(usuario ?? "").toUpperCase()}, SANTA CASA CUNHA
          </Text>
        </View>
      </View>
    </View>
  );
};

const CHUNK_SIZE = 12;

// Função utilitária para dividir o array em chunks de 12
function chunkArray<T>(array: T[], size: number): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
}

const ReciboDoacaoPDF: React.FC<ReciboDoacaoPDFProps> = (props) => {
  if (props.recibos && props.recibos.length > 0) {
    return (
      <Document>
        {props.recibos.map((recibo, index) => (
          <Page
            style={styles.page}
            orientation="portrait"
            key={`${index}-${recibo.numeroSequencial}`}
          >
            <ReciboHalf
              cpf={props.cpf}
              usuario={props.usuario}
              responsavel={props.responsavel}
              recibo={recibo}
            />
            <View style={extraStyles.hr} />
            <ReciboHalf
              cpf={props.cpf}
              usuario={props.usuario}
              responsavel={props.responsavel}
              recibo={recibo}
            />
          </Page>
        ))}
      </Document>
    );
  }

  // Sempre retorne algo válido
  return (
    <Document>
      <Page style={styles.page}>
        <View>
          <Text>Nenhum recibo disponível.</Text>
        </View>
      </Page>
    </Document>
  );
};

export default ReciboDoacaoPDF;
